import React, { useState, useEffect } from "react";
import QuillEditor from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Container, Card, CardContent, TextField, Button, Select, MenuItem, Switch, Typography, IconButton, CircularProgress, Tabs, Tab } from "@mui/material";
import { Save, Download, Search, Delete, DarkMode, LightMode } from "@mui/icons-material";
import axios from "axios";
import jsPDF from "jspdf";
import { CSVLink } from "react-csv";
import { FaMicrophone, FaPlus } from "react-icons/fa";
import API_CONFIG from '../../config/apiConfig';

const API_URL = `${API_CONFIG.baseURL}/notes/`;

const NotesPage = () => {
  const [content, setContent] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("SOAP Notes");
  const [autosave, setAutosave] = useState(true);
  const [notes, setNotes] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // For Multi-Tab Editing

  const templates = {
    "SOAP Notes": "Subjective:\nObjective:\nAssessment:\nPlan:\n",
    "CBT Therapy Notes": "Session Summary:\nCognitive Distortions Identified:\nHomework Assignment:\n",
    "Psychiatric Eval": "Chief Complaint:\nHistory of Present Illness:\nMedication Plan:\n",
  };

  useEffect(() => {
    fetchNotes();
  }, []);

  useEffect(() => {
    if (autosave) {
      const saveInterval = setInterval(() => {
        handleSaveNote();
      }, 10000);
      return () => clearInterval(saveInterval);
    }
  }, [content, autosave]);

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(API_URL);
      setNotes(response.data);
    } catch (error) {
      console.error("Error fetching notes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNote = async () => {
    try {
      await axios.post(API_URL, { content });
      fetchNotes();
    } catch (error) {
      console.error("Error saving note:", error);
    }
  };

  const handleDeleteNote = async (id) => {
    try {
      await axios.delete(`${API_URL}${id}/`);
      fetchNotes();
    } catch (error) {
      console.error("Error deleting note:", error);
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text(content, 10, 10);
    doc.save("Patient_Note.pdf");
  };

  const filteredNotes = notes.filter((note) =>
    note.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Container maxWidth="md" sx={{ mt: 4, bgcolor: darkMode ? "#121212" : "#fff", color: darkMode ? "#fff" : "#000", p: 3 }}>
      {/* Header */}
      <Card sx={{ bgcolor: darkMode ? "#222" : "#fff" }}>
        <CardContent>
          <Typography variant="h5">Patient Notes</Typography>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search notes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mt: 2 }}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1 }} />,
            }}
          />
          <IconButton sx={{ float: "right", mt: -7, mr: 2 }} onClick={() => setDarkMode(!darkMode)}>
            {darkMode ? <LightMode /> : <DarkMode />}
          </IconButton>
        </CardContent>
      </Card>

      {/* Template Selection */}
      <Card sx={{ mt: 3, bgcolor: darkMode ? "#222" : "#fff" }}>
        <CardContent>
          <Select fullWidth value={selectedTemplate} onChange={(e) => setSelectedTemplate(e.target.value)}>
            {Object.keys(templates).map((key) => (
              <MenuItem key={key} value={key}>
                {key}
              </MenuItem>
            ))}
          </Select>
          <Button onClick={() => setContent(templates[selectedTemplate])} variant="contained" sx={{ mt: 2 }}>
            Load Template
          </Button>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Autosave: <Switch checked={autosave} onChange={() => setAutosave(!autosave)} />
          </Typography>
        </CardContent>
      </Card>

      {/* Multi-Tab Editor */}
      <Tabs value={activeTab} onChange={(e, newTab) => setActiveTab(newTab)} sx={{ mt: 3 }}>
        <Tab label="Note 1" />
        <Tab label="New Note" icon={<FaPlus />} />
      </Tabs>

      {/* Rich Text Editor */}
      <Card sx={{ mt: 3, bgcolor: darkMode ? "#222" : "#fff" }}>
        <CardContent>
          <QuillEditor value={content} onChange={setContent} className="h-60" />
          <IconButton sx={{ mt: 2 }}><FaMicrophone size={20} /></IconButton>
        </CardContent>
      </Card>

      {/* Save & Export */}
      <Card sx={{ mt: 3, p: 2, display: "flex", justifyContent: "space-between", bgcolor: darkMode ? "#222" : "#fff" }}>
        <CSVLink data={notes} filename="notes.csv">
          <Button variant="outlined" startIcon={<Download />}>Export CSV</Button>
        </CSVLink>
        <Button variant="outlined" onClick={exportToPDF} startIcon={<Download />}>Export PDF</Button>
        <Button variant="contained" onClick={handleSaveNote} startIcon={<Save />}>Save Note</Button>
      </Card>
    </Container>
  );
};

export default NotesPage;
